import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Shield, Users, TrendingUp, IndianRupee, GraduationCap, Heart, Calendar,
  Check, X, Loader2, LogOut, RefreshCcw
} from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');

  useEffect(() => {
    if (user && user.role !== 'admin') { navigate('/dashboard'); return; }
    load();
  }, [user, navigate]);

  const load = async () => {
    try {
      const [s, p, a] = await Promise.all([
        apiService.adminStats(),
        apiService.adminPendingPayments(),
        apiService.adminAllPayments(),
      ]);
      setStats(s.data);
      setPending(p.data.payments);
      setAll(a.data.payments);
    } catch (e) { toast.error('Failed to load admin data'); }
    finally { setLoading(false); }
  };

  const verify = async (claimId, status) => {
    try {
      await apiService.adminVerifyPayment(claimId, { status });
      toast.success(`Payment ${status}`);
      load();
    } catch (e) { toast.error('Failed'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-12 h-12 animate-spin text-amber-500" /></div>;

  const items = tab === 'pending' ? pending : all;

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="admin-dashboard">
      <div className="bg-gradient-to-r from-amber-900 via-red-900 to-amber-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-red-500/10 rounded-full blur-3xl -translate-y-1/2"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center"><Shield className="w-7 h-7 text-white" /></div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-black text-white">Admin Console</h1>
                <p className="text-white/60 text-xs sm:text-sm">Welcome, {user?.username}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={load} className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold text-sm flex items-center gap-1.5"><RefreshCcw className="w-3 h-3" />Refresh</button>
              <button onClick={logout} className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl font-bold text-sm flex items-center gap-1.5"><LogOut className="w-4 h-4" />Logout</button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats grid */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
            <Stat icon={<Users />} label="Students" value={stats.total_students} color="from-blue-500 to-indigo-600" />
            <Stat icon={<IndianRupee />} label="Paid" value={stats.active_subscribers} color="from-green-500 to-emerald-600" />
            <Stat icon={<Calendar />} label="In Trial" value={stats.in_trial} color="from-orange-500 to-red-500" />
            <Stat icon={<GraduationCap />} label="Teachers" value={stats.teachers} color="from-indigo-500 to-purple-600" />
            <Stat icon={<Heart />} label="Parents" value={stats.parents} color="from-pink-500 to-rose-600" />
            <Stat icon={<TrendingUp />} label="Classes" value={stats.total_classes} color="from-cyan-500 to-blue-600" />
            <Stat icon={<Loader2 />} label="Pending" value={stats.pending_payments} color="from-amber-500 to-yellow-600" />
            <Stat icon={<Users />} label="New (7d)" value={stats.recent_signups_7d} color="from-purple-500 to-violet-600" />
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex border-b border-slate-100">
            <button onClick={() => setTab('pending')} data-testid="tab-pending"
              className={`px-5 py-3 font-bold text-sm transition ${tab === 'pending' ? 'bg-amber-50 text-amber-700 border-b-2 border-amber-500' : 'text-slate-500 hover:bg-slate-50'}`}>
              Pending Payments ({pending.length})
            </button>
            <button onClick={() => setTab('all')} data-testid="tab-all"
              className={`px-5 py-3 font-bold text-sm transition ${tab === 'all' ? 'bg-amber-50 text-amber-700 border-b-2 border-amber-500' : 'text-slate-500 hover:bg-slate-50'}`}>
              All Payments
            </button>
          </div>

          <div className="overflow-x-auto">
            {items.length === 0 ? (
              <div className="p-12 text-center text-slate-400 italic">No payments to display</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500 font-bold">
                  <tr>
                    <th className="px-4 py-3 text-left">Student</th>
                    <th className="px-4 py-3 text-left">UTR</th>
                    <th className="px-4 py-3 text-left">App</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-center">Status</th>
                    <th className="px-4 py-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map(p => (
                    <tr key={p.id} className="hover:bg-slate-50 transition">
                      <td className="px-4 py-3 font-bold text-[#1E293B] truncate max-w-[180px]">{p.student_email}</td>
                      <td className="px-4 py-3 font-mono text-xs">{p.transaction_ref}</td>
                      <td className="px-4 py-3 text-xs">{p.upi_app || '—'}</td>
                      <td className="px-4 py-3 text-right font-bold">₹{p.amount}</td>
                      <td className="px-4 py-3 text-xs text-slate-500">{new Date(p.claimed_at).toLocaleDateString()}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 rounded-full text-[10px] font-bold uppercase ${
                          p.status === 'verified' ? 'bg-green-100 text-green-700' :
                          p.status === 'rejected' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>{p.status}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {p.status === 'pending_verification' ? (
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => verify(p.id, 'verified')} data-testid={`verify-${p.id}`}
                              className="px-2 py-1 bg-green-500 hover:bg-green-600 text-white rounded-md text-xs font-bold flex items-center gap-1">
                              <Check className="w-3 h-3" />Verify
                            </button>
                            <button onClick={() => verify(p.id, 'rejected')} data-testid={`reject-${p.id}`}
                              className="px-2 py-1 bg-red-500 hover:bg-red-600 text-white rounded-md text-xs font-bold flex items-center gap-1">
                              <X className="w-3 h-3" />Reject
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ icon, label, value, color }) => (
  <div className={`bg-gradient-to-br ${color} text-white rounded-2xl p-3 shadow-md`}>
    <div className="opacity-80 mb-1 w-4 h-4">{icon}</div>
    <p className="text-xl font-black leading-none">{value}</p>
    <p className="text-[10px] opacity-80 mt-1">{label}</p>
  </div>
);

export default AdminDashboard;
